import styled from 'styled-components'
import MoonScene, { MoonIcon, nightSky } from '../art/MoonScene'
import { Display } from '../ui'

const Wrap = styled.div<{ $boxed?: boolean }>`
  position: relative;
  display: grid;
  justify-items: center;
  text-align: center;
  gap: 4px;
  padding: 0 16px 20px;
  overflow: hidden;
  border-radius: 20px;
  /* inside a panel the state has no frame of its own: panels never nest */
  border: ${({ theme, $boxed }) => ($boxed ? `1px dashed ${theme.colors.cardBorder}` : '0')};
`
const Sky = styled.div`
  position: relative;
  /* the spot scene is drawn for a 420 x 232 box and fades into the panel at its edges, so a wide panel never crops it */
  width: min(100%, 420px);
  aspect-ratio: 420 / 232;
  min-height: 150px; /* where aspect-ratio is not supported */
  margin: 0 auto 8px;
  ${nightSky}
  /* the ellipse ends inside the box on every side, so the scene's rectangle never shows against the panel */
  mask-image: radial-gradient(ellipse 50% 56% at 50% 56%, #000 42%, transparent 100%);
  -webkit-mask-image: radial-gradient(ellipse 50% 56% at 50% 56%, #000 42%, transparent 100%);
`
/* the compact state: the moon alone (its halo fades out inside the 120 px box), pulled in so it costs about 70 px of height */
const Moon = styled.div`
  margin: -20px 0 -22px;
  display: flex;
  justify-content: center;
`
const Body = styled.p`
  margin: 0;
  max-width: 440px;
  font-size: 15px;
  line-height: 22px;
  color: ${({ theme }) => theme.colors.textSubtle};
`
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
`

/** The empty and loading state of the token pages: the husky howls at the moon. One message, at most two actions. */
export default function WolfState({
  title,
  body,
  actions,
  busy,
  testId,
  boxed,
  compact,
}: {
  title: string
  body?: React.ReactNode
  actions?: React.ReactNode
  busy?: boolean
  testId?: string
  boxed?: boolean
  /** the moon alone instead of the full scene: for the secondary empty states, so the howling wolf stays a rare moment on a page */
  compact?: boolean
}) {
  return (
    <Wrap
      $boxed={boxed}
      data-testid={testId ?? 'tp-state'}
      aria-busy={busy || undefined}
      role={busy ? 'status' : undefined}
    >
      {compact ? (
        <Moon aria-hidden="true">
          <MoonIcon />
        </Moon>
      ) : (
        <Sky>
          <MoonScene scene="spot" align="xMidYMax" />
        </Sky>
      )}
      <Display as="h3" bold fontSize="20px" lineHeight="26px">
        {title}
      </Display>
      {body && <Body>{body}</Body>}
      {actions && <Actions>{actions}</Actions>}
    </Wrap>
  )
}
